/**
 * Client IMAP minimal, au-dessus d'un tunnel { lire, ecrire, fermer }.
 *
 * Une commande à la fois, ce qui suffit à Scanyt et simplifie tout : on écrit,
 * puis on lit jusqu'à la réponse étiquetée. Les littéraux sont gérés dans les
 * deux sens (le serveur en renvoie dans les FETCH ; on en envoie pour les
 * recherches accentuées).
 */
import { decouper, MARQUE_LITTERAL } from './syntaxe.js';

const encodeur = new TextEncoder();
const decodeur = new TextDecoder();

// Un serveur muet aussi longtemps est considéré comme perdu. Les recherches
// Gmail sur une grosse boîte peuvent prendre une bonne minute.
const DELAI_SILENCE_MS = 180000;

export class ErreurImap extends Error {
  constructor(statut, texte) {
    super(texte || statut);
    this.statut = statut;
  }
}

export class ClientImap {
  constructor(tunnel) {
    this.tunnel = tunnel;
    this.tampon = new Uint8Array(0);
    this.compteur = 0;
    this.capacites = new Set();
  }

  // --- Lecture ------------------------------------------------------------

  async #remplir() {
    let minuterie;
    const morceau = await Promise.race([
      this.tunnel.lire(),
      new Promise((_, ko) => {
        minuterie = setTimeout(() => ko(new Error('Le serveur mail ne répond plus')), DELAI_SILENCE_MS);
      })
    ]).finally(() => clearTimeout(minuterie));
    if (!morceau) throw new Error('Le serveur mail a fermé la connexion');
    const reste = this.tampon;
    const nouveau = new Uint8Array(reste.length + morceau.length);
    nouveau.set(reste);
    nouveau.set(morceau, reste.length);
    this.tampon = nouveau;
  }

  async #lireOctets(n) {
    while (this.tampon.length < n) await this.#remplir();
    const octets = this.tampon.slice(0, n);
    this.tampon = this.tampon.subarray(n);
    return octets;
  }

  async #lireLigneBrute() {
    let depuis = 0;
    for (;;) {
      const fin = this.tampon.indexOf(10, depuis);
      if (fin !== -1) {
        const ligne = decodeur.decode(this.tampon.subarray(0, fin)).replace(/\r$/, '');
        this.tampon = this.tampon.subarray(fin + 1);
        return ligne;
      }
      depuis = this.tampon.length;
      await this.#remplir();
    }
  }

  /**
   * Une réponse complète : { texte, litteraux }. Dans `texte`, chaque littéral
   * est remplacé par MARQUE_LITTERAL ; ses octets sont dans `litteraux`.
   */
  async #lireReponse() {
    let texte = '';
    const litteraux = [];
    for (;;) {
      const ligne = await this.#lireLigneBrute();
      const annonce = ligne.match(/\{(\d+)\+?\}$/);
      if (!annonce) return { texte: texte + ligne, litteraux };
      texte += ligne.slice(0, annonce.index) + MARQUE_LITTERAL;
      litteraux.push(await this.#lireOctets(Number(annonce[1])));
    }
  }

  // --- Commandes ----------------------------------------------------------

  async accueil() {
    const { texte } = await this.#lireReponse();
    if (!/^\* (OK|PREAUTH)/i.test(texte)) throw new Error(`Accueil inattendu : ${texte}`);
    this.#noterCapacites(texte);
    return texte;
  }

  #noterCapacites(texte) {
    const m = texte.match(/^\* CAPABILITY (.*)$/i) || texte.match(/\[CAPABILITY ([^\]]*)\]/i);
    if (m) this.capacites = new Set(m[1].toUpperCase().split(/\s+/).filter(Boolean));
  }

  /**
   * Envoie une commande et produit ses réponses non étiquetées au fil de l'eau,
   * sous forme { texte, jetons() }. `morceaux` mêle texte et { litteral }.
   * Lève une ErreurImap si le serveur répond NO ou BAD.
   */
  async *flux(morceaux) {
    const etiquette = `A${++this.compteur}`;
    const parties = [`${etiquette} `, ...(Array.isArray(morceaux) ? morceaux : [morceaux]), '\r\n'];

    // Le texte est envoyé jusqu'à chaque littéral, puis on attend l'invitation
    // « + » du serveur avant d'envoyer ses octets.
    let enCours = '';
    for (const partie of parties) {
      if (typeof partie === 'string') { enCours += partie; continue; }
      await this.tunnel.ecrire(encodeur.encode(`${enCours}{${partie.litteral.length}}\r\n`));
      enCours = '';
      for (;;) {
        const reponse = await this.#lireReponse();
        if (reponse.texte.startsWith('+')) break;
        if (reponse.texte.startsWith(`${etiquette} `)) {
          const [, statut, reste] = reponse.texte.match(/^\S+ (\S+) ?(.*)$/);
          throw new ErreurImap(statut.toUpperCase(), reste);
        }
      }
      await this.tunnel.ecrire(partie.litteral);
    }
    await this.tunnel.ecrire(encodeur.encode(enCours));

    for (;;) {
      const reponse = await this.#lireReponse();
      if (reponse.texte.startsWith(`${etiquette} `)) {
        const [, statut, reste] = reponse.texte.match(/^\S+ (\S+) ?(.*)$/);
        if (statut.toUpperCase() !== 'OK') throw new ErreurImap(statut.toUpperCase(), reste);
        this.#noterCapacites(reponse.texte);
        return;
      }
      if (reponse.texte.startsWith('+')) continue;
      this.#noterCapacites(reponse.texte);
      yield {
        texte: reponse.texte,
        jetons: () => decouper(reponse.texte, reponse.litteraux)
      };
    }
  }

  /** Comme flux(), mais rassemble toutes les réponses. */
  async commande(morceaux) {
    const reponses = [];
    for await (const r of this.flux(morceaux)) reponses.push(r);
    return reponses;
  }

  /**
   * AUTHENTICATE avec réponse initiale (SASL-IR), pour XOAUTH2. En cas de
   * refus, le serveur envoie d'abord une invitation « + » avec le détail de
   * l'erreur, à laquelle on répond par une ligne vide, puis le NO final.
   */
  async authentifier(mecanisme, reponseInitiale) {
    const etiquette = `A${++this.compteur}`;
    await this.tunnel.ecrire(encodeur.encode(`${etiquette} AUTHENTICATE ${mecanisme} ${reponseInitiale}\r\n`));
    let detail = '';
    for (;;) {
      const reponse = await this.#lireReponse();
      if (reponse.texte.startsWith(`${etiquette} `)) {
        const [, statut, reste] = reponse.texte.match(/^\S+ (\S+) ?(.*)$/);
        if (statut.toUpperCase() !== 'OK') {
          throw new ErreurImap(statut.toUpperCase(), detail ? `${reste} ${detail}`.trim() : reste);
        }
        this.#noterCapacites(reponse.texte);
        return;
      }
      if (reponse.texte.startsWith('+')) {
        try { detail = atob(reponse.texte.slice(1).trim()); } catch { detail = ''; }
        await this.tunnel.ecrire(encodeur.encode('\r\n'));
        continue;
      }
      this.#noterCapacites(reponse.texte);
    }
  }

  async deconnexion() {
    try { await this.commande('LOGOUT'); } catch { /* déjà fermé */ }
    this.tunnel.fermer();
  }
}
